import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Page from '../../../../components/Page';
import useSettings from '../../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { fCurrency } from '../../../../utils/formatNumber';
import { dispatch, useSelector } from '../../../../redux/store';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { Autocomplete, Card, Container, Grid, Stack, TextField, Typography } from '@mui/material';
import { getCashFlowLedger, getCashFlowSubCategories, verifyDrawerCloseAmounts } from '../../../../redux/slices/financeRedux/cashFlowRedux';
import { utcMoment } from 'src/utils/common';

export default function SaleDepositVerification() {
  const { themeStretch } = useSettings();
  let locationList;
  let selectedLocation;
  ({ data: locationList, selectedLocation } = useSelector((state) => state.location));
  const { loggedInUser } = useSelector((state) => state.user);

  const dataModel = {
    selectedLocationId: selectedLocation?.id,
    toData: [],
    amount: null,
    selectedSupplierOrderIds: [],
    title: '',
    description: '',
  };

  const [selectedData, setSelectedData] = useState(dataModel);
  const { data, addData, cashFlowSubCategories } = useSelector((state) => state.cashFlow);

  const [fromLedgerList, setFromLedgerList] = useState([]);
  const [toLedgerList, setToLedgerList] = useState([]);
  const [verifiableOrderList, setVerifiableOrderList] = useState([]);

  const updateDataOnObj = (key, value) => {
    setSelectedData({ ...selectedData, [key]: value });
  };

  useEffect(() => {
    if (selectedLocation?.id) {
      setSelectedData((prev) => ({ ...prev, selectedLocationId: selectedLocation?.id }));
    }
  }, [selectedLocation]);

  useEffect(() => {
    setFromLedgerList(cashFlowSubCategories?.filter((value) => [67, 68, 69, 106, 107, 108, 109].includes(value?.id)));
  }, [cashFlowSubCategories]);

  useEffect(() => {
    setVerifiableOrderList(data?.filter((value) => Number(value?.isDeposited) === 0));
  }, [data]);

  useEffect(() => {
    let allowedIdList = [];
    const ledgerIdMap = {
      67: [110, 111, 112, 113, 121, 123, 124, 125, 126, 127, 128],
      68: [110, 111, 112, 119, 123, 124, 125, 126, 127, 128],
      69: [110, 111, 112, 118, 123, 124, 125, 126, 127, 128],
      106: [110, 111, 112, 117, 123, 124, 125, 126, 127, 128],
      107: [110, 111, 112, 116, 123, 124, 125, 126, 127, 128],
      108: [110, 111, 112, 123, 124, 125, 126, 127, 128],
      109: [110, 111, 112, 123, 124, 125, 126, 127, 128],
    };

    if (ledgerIdMap[selectedData?.fromLedgerId]) {
      allowedIdList = ledgerIdMap[selectedData?.fromLedgerId];
      setSelectedData((prevState) => ({
        ...prevState,
        toData: allowedIdList.map((id) => ({
          id,
          name: cashFlowSubCategories?.find((sub) => sub?.id === id)?.name,
          locationId: null,
          amount: null,
          date: null,
        })),
      }));
    }

    setToLedgerList(cashFlowSubCategories?.filter((value) => allowedIdList.includes(value?.id)));
  }, [selectedData?.fromLedgerId, cashFlowSubCategories]);

  useEffect(() => {
    dispatch(getCashFlowLedger(selectedLocation?.id, 7));
  }, [selectedLocation]);

  useEffect(() => {
    dispatch(getCashFlowSubCategories());
  }, []);

  useEffect(() => {
    if (addData?.time) {
      dispatch(getCashFlowLedger(selectedLocation?.id, 7));
      setSelectedData(dataModel);
    }
  }, [addData]);

  const handleSubmit = () => {
    if (!selectedData.title?.length) return toast.error('Title is required!');
    if (!selectedData.description?.length) return toast.error('Description is required!');
    if (selectedData.amount === 0) return toast.error('Amount is required!');

    const invalid = selectedData.toData.some((item) => item?.amount && (!item?.locationId || !item?.date));
    if (invalid) return toast.error('Please select a location and date/time for each entry with an amount.');

    dispatch(verifyDrawerCloseAmounts(selectedData));
  };

  const isPossibleToVerify = () => {
    const total = selectedData?.toData?.reduce((sum, item) => sum + Number(item?.amount || 0), 0);
    return Number(selectedData?.amount || 0) === total;
  };

  return (
    <Page title={`Sales Deposit Verification`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Sales Deposit Verification`}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Finance', href: '' }, { name: `Sales Deposit Verification` }]}
          action={
            <LoadingButton variant='contained' onClick={handleSubmit} disabled={!isPossibleToVerify()}>
              Verify & Record
            </LoadingButton>
          }
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant='subtitle1'>From Payment Information</Typography>
              <Stack spacing={3}>
                <Autocomplete
                  onChange={(e, newValue) => updateDataOnObj('fromLedgerId', newValue?.value)}
                  options={fromLedgerList.map((item) => ({ label: item.name, value: item.id }))}
                  value={
                    fromLedgerList.find((item) => item.id === selectedData.fromLedgerId) && {
                      label: fromLedgerList.find((item) => item.id === selectedData.fromLedgerId)?.name,
                      value: selectedData.fromLedgerId,
                    }
                  }
                  renderInput={(params) => <TextField label='Select Ledger' {...params} />}
                />

                {selectedData?.fromLedgerId && (
                  <Autocomplete
                    multiple
                    onChange={(e, newValue) => {
                      const selectedIds = newValue.map((v) => v.value);
                      const selectedOrders = verifiableOrderList.filter((x) => selectedIds.includes(x.id));
                      const totalAmount = selectedOrders.reduce((sum, order) => sum + Number(order.amount || 0), 0);

                      setSelectedData({
                        ...selectedData,
                        selectedSupplierOrderIds: selectedIds,
                        amount: totalAmount,
                        title: `${loggedInUser?.name} verified Orders: ${selectedIds.join(', ')}`,
                        description: `Verified ${selectedIds.length} orders by ${loggedInUser?.name}`,
                      });
                    }}
                    options={verifiableOrderList
                      ?.filter((x) => x?.debitLedgerId === selectedData.fromLedgerId)
                      ?.map((value) => ({
                        label: `(${fCurrency(value?.amount)}) - (${utcMoment(value?.datetime).format('DD MMM YYYY')}) - ${value?.debitLocationName} (ID: ${value?.id})`,
                        value: value?.id,
                      }))}
                    value={verifiableOrderList
                      ?.filter((x) => selectedData.selectedSupplierOrderIds?.includes(x.id))
                      ?.map((value) => ({
                        label: `(${fCurrency(value?.amount)}) - (${utcMoment(value?.datetime).format('DD MMM YYYY')}) - ${value?.debitLocationName} (ID: ${value?.id})`,
                        value: value?.id,
                      }))}
                    renderInput={(params) => <TextField label='Select Orders' {...params} />}
                  />
                )}

                {selectedData?.title && (
                  <>
                    <hr />
                    <h3>
                      <span style={{ color: 'gray' }}>Description:</span> {selectedData.title}
                    </h3>
                    <h3>
                      <span style={{ color: 'gray' }}>Total Amount:</span> {fCurrency(selectedData.amount)}
                    </h3>
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      Orders: {selectedData.selectedSupplierOrderIds?.join(', ')}
                    </Typography>
                  </>
                )}
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <Typography variant='subtitle1'>To Payment Information</Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Stack spacing={3}>
                  {selectedData?.toData?.map((item, index) => (
                    <div key={index}>
                      <Typography variant='subtitle2' gutterBottom>
                        {item.name}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Autocomplete
                            onChange={(e, newValue) => {
                              setSelectedData((prev) => ({
                                ...prev,
                                toData: prev.toData.map((d, i) => (i === index ? { ...d, locationId: newValue?.value } : d)),
                              }));
                            }}
                            options={locationList.map((l) => ({ label: l.name, value: l.id }))}
                            value={
                              item.locationId
                                ? {
                                    label: locationList.find((l) => l.id === item.locationId)?.name,
                                    value: item.locationId,
                                  }
                                : null
                            }
                            renderInput={(params) => <TextField label='Location' {...params} />}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <TextField
                            label='Amount'
                            type='number'
                            fullWidth
                            value={item.amount || ''}
                            onChange={(e) => {
                              const amount = e.target.value;
                              setSelectedData((prev) => ({
                                ...prev,
                                toData: prev.toData.map((d, i) => (i === index ? { ...d, amount } : d)),
                              }));
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <DateTimePicker
                            label='Date & Time'
                            value={item.date ? moment(item.date, 'YYYY-MM-DD HH:mm:ss') : null}
                            onChange={(date) => {
                              setSelectedData((prev) => ({
                                ...prev,
                                toData: prev.toData.map((d, i) => (i === index ? { ...d, date: moment(date).format('YYYY-MM-DD HH:mm:ss') } : d)),
                              }));
                            }}
                            renderInput={(params) => <TextField fullWidth {...params} />}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  ))}
                </Stack>
              </LocalizationProvider>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
